import React from 'react';
import { useWikiQuery, wikiKeys } from '../shared/useWikiQueries';
import WikiIcon from '../shared/WikiIcon';
import { WikiLoading } from '../shared/WikiStates';
import { dayTimeLabel } from '../shared/wikiFormat';
import { askErrorText } from './askStore';
import { AskLines } from './AskParts';
import { SourceGrid } from './AskSources';
import { UserBubble } from './AskTurn';

/**
 * `/wiki/ask?handoff={id}` — the conversation snapshot an "Ask {owner}"
 * hand-off sent to the page owner (bell link, contracts/BE-ask.md). Not in
 * the prototype (it only toasts); without it the bell link would land the
 * owner on an empty Ask. Read-only: questions, answer lines/text and the
 * cited pages the recipient can read.
 */
export default function AskHandoff({ handoffId, meId, onClose }) {
    const query = useWikiQuery(
        wikiKeys.ask('handoff', handoffId),
        handoffId ? `/wiki/ask/handoffs/${encodeURIComponent(handoffId)}` : null,
        { retry: false },
    );

    const close = (
        <button
            type="button"
            className="wk-btn wk-btn--ghost wk-btn--icon"
            aria-label="Close this conversation"
            title="Close"
            onClick={onClose}
        >
            <WikiIcon name="x" size={15} />
        </button>
    );

    if (query.isLoading) {
        return (
            <section className="wk-ask-card wk-ask-handoff" data-testid="wiki-ask-handoff">
                <WikiLoading label="Loading the conversation…" />
            </section>
        );
    }
    if (query.isError || !query.data) {
        return (
            <section className="wk-ask-card wk-ask-handoff" data-testid="wiki-ask-handoff" role="alert">
                <div className="wk-ask-handoff-head">
                    <span className="wk-ask-gap-text">{askErrorText(query.error)}</span>
                    <span className="wk-spacer" />
                    {close}
                </div>
            </section>
        );
    }

    const data = query.data;
    const sentByMe = Boolean(meId && data.from?.user_id === meId);
    const title = sentByMe
        ? `You sent this conversation to ${data.owner?.name || 'the owner'}`
        : `${data.from?.name || 'Someone'} sent you this conversation`;
    const turns = Array.isArray(data.turns) ? data.turns : [];

    return (
        <section className="wk-ask-card wk-ask-handoff" data-testid="wiki-ask-handoff">
            <div className="wk-ask-handoff-head">
                <span className="wk-ask-tile wk-ask-tile--teal" aria-hidden="true">
                    <WikiIcon name="user" size={15} />
                </span>
                <div className="wk-ask-handoff-title">
                    <div className="wk-ask-handoff-name">{title}</div>
                    {data.created_at && <div className="wk-ask-handoff-meta">{dayTimeLabel(data.created_at)}</div>}
                </div>
                {close}
            </div>
            {data.note && <div className="wk-ask-note wk-ask-handoff-note">{data.note}</div>}
            <div className="wk-ask-handoff-turns">
                {turns.map((turn, index) => (
                    // Snapshot turns carry no id; their order is the conversation's.
                    <div key={`${index}-${turn?.question}`} className="wk-ask-handoff-turn">
                        <UserBubble text={turn?.question} live={false} />
                        <div className="wk-ask-handoff-answer">
                            {Array.isArray(turn?.lines) && turn.lines.length > 0
                                ? <AskLines lines={turn.lines} />
                                : (turn?.text && <p className="wk-ask-gap-text">{turn.text}</p>)}
                        </div>
                        <SourceGrid sources={turn?.sources} turnId={`handoff-${index}`} readOnly />
                    </div>
                ))}
            </div>
        </section>
    );
}
